import { FC, useMemo } from 'react';
import { Formik, Form } from 'formik';
import { Box } from '@mui/material';

import { SearchIcon } from '../../../../../components/Icons';
import { TypeIcon } from '../../../../../components/Icons/TypeIcon';

import {
  PlacesSearchFormData,
  PLACES_SEARCH_INITIAL_VALUES,
  PLACES_FORM_SORT_OPTIONS,
} from '../../types/placesSearch.types';
import type { PlacesLocationDetails } from '../../types/placesLocation.types';
import { useGetPlacesTypesQuery } from '../../../../../store/services/ideasApi';
import { selectablePlaceTypeOptions } from '../../utils/placesSearch.utils';

import styles from './PlacesSearchForm.module.scss';
import HotelLocationField, { HotelLocationPlaceDetails } from '../HotelSearchForm/HotelLocationField';
import PillSelectField from '../PillSelectField/PillSelectField';
import CustomButton from '../../../../../common/CustomButton';

type PlacesSearchFormProps = {
  onSubmit?: (values: PlacesSearchFormData) => void | Promise<void>;
  isLoading?: boolean;
  isMobileLayout?: boolean;
};

function toLocationDetails(details: HotelLocationPlaceDetails | null): PlacesLocationDetails | undefined {
  if (!details) return undefined;
  return {
    countryCode: details.countryCode,
    city: details.city,
    latitude: details.latitude,
    longitude: details.longitude,
    placeId: details.placeId,
  };
}

const PlacesSearchForm: FC<PlacesSearchFormProps> = ({ onSubmit, isLoading = false, isMobileLayout = false }) => {
  const { data: apiPlaceTypes = [] } = useGetPlacesTypesQuery();

  const placeTypeOptions = useMemo(() => selectablePlaceTypeOptions(apiPlaceTypes), [apiPlaceTypes]);

  const validate = (values: PlacesSearchFormData) => {
    const errors: Partial<Record<keyof PlacesSearchFormData, string>> = {};
    if (!values.location.trim()) {
      errors.location = 'Location is required';
    } else {
      const city = values.location_details?.city?.trim();
      const cc = values.location_details?.countryCode?.trim();
      if (!city || !cc) {
        errors.location = 'Pick a city from the suggestions';
      }
    }
    const pt = values.placeType.trim().toLowerCase();
    if (!pt || pt === 'all') {
      errors.placeType = 'Select a place type';
    }
    return errors;
  };

  return (
    <Box className={styles.placesSearchFormContainer}>
      <Formik
        initialValues={PLACES_SEARCH_INITIAL_VALUES}
        validate={validate}
        onSubmit={async (values, { resetForm }) => {
          await Promise.resolve(onSubmit?.(values));
          resetForm({
            values: { ...values },
            submitCount: 0,
            touched: {},
            errors: {},
          });
        }}
        enableReinitialize
      >
        {({ values, handleChange, errors, submitCount, setFieldValue }) => {
          const showSubmitErrors = submitCount > 0;
          const reserveDesktopSlots = !isMobileLayout;

          return (
            <Form className={styles.tripForm}>
              <Box className={styles.formGrid}>
                <Box className={styles.formField}>
                  <HotelLocationField
                    name="location"
                    value={values.location}
                    onChange={handleChange}
                    error={showSubmitErrors && errors.location ? errors.location : undefined}
                    inputLabel="Location"
                    placeholder="City"
                    discoverChrome
                    compactForMobile={isMobileLayout}
                    allowClear
                    placeTypes={['(cities)']}
                    onPlaceSelected={details => {
                      if (!details?.city?.trim() || !details?.countryCode?.trim()) {
                        setFieldValue('location_details', undefined, true);
                        return;
                      }
                      setFieldValue('location_details', toLocationDetails(details), true);
                    }}
                  />
                </Box>

                <Box className={styles.formField}>
                  <PillSelectField
                    name="placeType"
                    inputLabel="Type"
                    value={values.placeType}
                    onChange={e => setFieldValue('placeType', e.target.value as string)}
                    options={placeTypeOptions}
                    startIcon={<TypeIcon size={20} />}
                    compactForMobile={isMobileLayout}
                    placeholder="Select the type"
                    error={showSubmitErrors && errors.placeType ? errors.placeType : undefined}
                  />
                </Box>

                <Box className={styles.formField}>
                  <PillSelectField
                    name="sortBy"
                    inputLabel="Sort By"
                    value={values.sortBy}
                    onChange={e => setFieldValue('sortBy', e.target.value as PlacesSearchFormData['sortBy'])}
                    options={PLACES_FORM_SORT_OPTIONS}
                    compactForMobile={isMobileLayout}
                  />
                </Box>

                <Box
                  className={`${styles.submitButtonWrapper} ${isMobileLayout ? styles.submitButtonWrapperWide : ''}`}
                >
                  <CustomButton
                    btnType="submit"
                    variant="contained"
                    loading={isLoading}
                    startIcon={
                      <SearchIcon
                        color="#FFFFFF"
                        size={24}
                      />
                    }
                  >
                    {isMobileLayout ? 'Search Places' : ''}
                  </CustomButton>
                  {reserveDesktopSlots ? (
                    <Box
                      className={styles.fieldErrorReserve}
                      aria-hidden
                    />
                  ) : null}
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default PlacesSearchForm;
